import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { Link } from 'react-router'
import type { ApiFailure, ApiResult, Schema } from '@/api/client'
import { useBrand } from '@/brand/context'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { TIMES, WEEKDAYS, pad, zoneName } from './cadence'
import type { Schedule } from './cadence'

export type Condition = Schema<'ConditionOut'>
export type { Schedule } from './cadence'
type Frequency = Schedule['frequency']
type Scope = Schedule['scope']

/**
 * The run schedule as one form — *On a schedule* and *Automatically, when
 * something changes* over one site's row, with the one Save that writes both.
 *
 * Settings consolidation (2026-09-15): extracted from `pages/seo/RunTriggers`'
 * `TriggerCards`, which kept it beside *By hand*. The page keeps *By hand* and a
 * read-only summary; this form is rendered by `/settings/run-schedule` inside a
 * `SettingRow` and by the wizard step full width, so the fields exist once.
 *
 * `domain` is in the form's accessible name so the site being edited is on
 * screen while typing — the panel's site chip is a heading away.
 */
export function RunScheduleForm({
  schedule,
  domain,
  automationAvailable,
  onSubmit,
  submitLabel,
}: {
  schedule: Schedule
  domain: string
  /** `tenant.automation_available` — served on the organisation, never worked out here. */
  automationAvailable: boolean
  onSubmit: (schedule: Schedule) => Promise<ApiResult<Schedule>>
  submitLabel: string
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const [draft, setDraft] = useState<Schedule>(schedule)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  // The server is the authority: a save returns the row as stored — thresholds
  // clamped, conditions normalised — and the form has to show what was kept
  // rather than what was typed.
  useEffect(() => {
    setDraft(schedule)
  }, [schedule])

  const dirty = JSON.stringify(draft) !== JSON.stringify(schedule)
  const enabledCount = draft.conditions.filter((condition) => condition.enabled).length
  // The *saved* schedule's prediction, not the draft's: an unsaved change to the
  // time has not moved when it will actually fire, and showing the draft's would
  // tell the tenant a run was coming that nothing has been told about.
  const nextRunAt = schedule.next_run_at ?? null

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    // Enabled as drawn (parity P-TRIG-06); a press with nothing changed says so
    // rather than re-registering an AWS schedule.
    if (!dirty) {
      toast.show({ kind: 'info', title: 'Nothing to save' })
      return
    }
    setBusy(true)
    setFailure(null)
    const result = await onSubmit(draft)
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form
      className="ar-stack ar-stack--loose"
      aria-label={`Run schedule — ${domain}`}
      onSubmit={(event) => void submit(event)}
    >
      {/*
        Unattended runs need a live subscription (2026-09-10). Both cards below
        configure work that fires while nobody is watching, so the notice sits
        above them rather than inside one — and the settings themselves stay
        editable, because what a tenant sets up during a trial is exactly what
        should start running the moment they pay.
      */}
      {automationAvailable ? null : (
        <div className="ar-alert ar-alert--warning">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>Automatic and scheduled runs start when you choose a plan</strong>
            Your trial includes one unattended pass so you can see one run on its own; after that,
            what you set up here begins the moment you subscribe. Runs you start yourself work as
            normal. <Link to="/billing">See the plans</Link>
          </div>
        </div>
      )}

      {/* --- on a schedule ------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>On a schedule</h2>
          <span className="ar-card__spacer" />
          <label className="ar-switch">
            <input
              type="checkbox"
              aria-label="Run on a schedule"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked })
              }}
            />
            <span className="ar-switch__track" aria-hidden="true" />
          </label>
        </div>
        <div className="ar-card__body ar-stack">
          {draft.enabled && !schedule.synced && schedule.sync_error !== null ? (
            <div className="ar-alert ar-alert--warning" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>This schedule is saved but not live yet</strong>
                {brand.productName} could not register it with the scheduler, so it will not fire
                until that is retried. {schedule.sync_error}
              </div>
            </div>
          ) : null}

          <div className="ar-grid ar-grid--2 app-triggers__when">
            <Field label="How often">
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={draft.frequency}
                  onChange={(event) => {
                    const frequency = event.target.value as Frequency
                    setDraft({
                      ...draft,
                      frequency,
                      // A weekday is required for `weekly` and forbidden otherwise —
                      // `ck_run_schedules_weekly_has_weekday`, and `Schedule.validate`
                      // refuses the mismatch before anything is written. Monday is the
                      // default because it is the day the design's own copy shows.
                      weekday: frequency === 'weekly' ? (draft.weekday ?? 1) : null,
                    })
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekdays">Weekdays only</option>
                  <option value="weekly">Weekly</option>
                </select>
              )}
            </Field>

            <Field
              label="At"
              help={`${zoneName(draft.timezone)} — your organisation's time zone, never the server's.`}
            >
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={`${pad(draft.hour)}:${pad(draft.minute)}`}
                  onChange={(event) => {
                    const [hour, minute] = event.target.value.split(':')
                    setDraft({ ...draft, hour: Number(hour), minute: Number(minute) })
                  }}
                >
                  {TIMES.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          {draft.frequency === 'weekly' ? (
            <Field label="On">
              {(props) => (
                <select
                  {...props}
                  className="ar-select"
                  value={String(draft.weekday ?? 1)}
                  onChange={(event) => {
                    setDraft({ ...draft, weekday: Number(event.target.value) })
                  }}
                >
                  {WEEKDAYS.map((day, index) => (
                    <option key={day} value={index + 1}>
                      {day}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          <Field
            label="What a scheduled run does"
            help="Drafting is the expensive half. A run that only checks ranks costs about ₹6; a full run costs about ₹18."
          >
            {(props) => (
              <select
                {...props}
                className="ar-select"
                value={draft.scope}
                onChange={(event) => {
                  setDraft({ ...draft, scope: event.target.value as Scope })
                }}
              >
                <option value="full">Audit, rank check, and draft content</option>
                <option value="audit_rank">Audit and rank check only</option>
                <option value="rank_only">Rank check only — cheapest</option>
              </select>
            )}
          </Field>

          {schedule.enabled && nextRunAt !== null ? (
            <p className="ar-subtle" style={{ margin: 0 }}>
              Next run {relativeTime(nextRunAt, new Date())}. Predicted — the scheduler is the
              authority.
            </p>
          ) : null}
        </div>
      </section>

      {/* --- automatically -------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Automatically, when something changes</h2>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">
            {enabledCount} of {draft.conditions.length} on
          </span>
        </div>
        <div className="ar-card__body ar-stack">
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>An automatic run still cannot publish anything</strong>
              It produces drafts and technical fixes, and whatever you gated stays gated. The two
              settings are independent on purpose.
            </div>
          </div>

          {draft.conditions.map((condition) => (
            <ConditionRow
              key={condition.name}
              condition={condition}
              onChange={(next) => {
                setDraft({
                  ...draft,
                  conditions: draft.conditions.map((row) => (row.name === next.name ? next : row)),
                })
              }}
            />
          ))}

          {/* SEO-UI-73. What replaces the design's per-site cap input: the one
              cap there is, named, with the way to it. The row was answered *no
              automatic cap* — see the module docstring — and a screen that just
              dropped the control would leave the question the drawing raised
              hanging, which is the half of an omission that costs something.

              The sentence is careful about scope on purpose. The prototype's
              help says automatic runs stop and *"scheduled ones carry on"*,
              which would be true of a second ceiling and is not true of this
              one: `app/runs/executor.py` refuses a stage on the ledger's ceiling
              whatever triggered the pass. Repeating the prototype's wording here
              would have been a promise that a schedule survives the cap. */}
          <hr className="app-gate__rule" />
          <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
            {/* One string: the line break before `{brand.productName}` rendered
                "reachedAugmentrait Private Limited stops" — the third of its kind,
                found by `web/src/test/jsx-joins.test.ts` on 2026-09-20. */}
            {`Spending is capped for the whole organisation, not per site — when that cap is reached ${brand.productName} stops, whether the run was automatic, scheduled, or one you started.`}{' '}
            <Link to="/settings/budget">Set the budget cap</Link>.
          </p>
        </div>
        <div className="ar-card__footer">
          <InlineError failure={failure} />
          <span className="ar-card__spacer" />
          {/* SEO-UI-108. `tone="primary"`, stated rather than defaulted — this
              is the screen's only Save, and omitting the prop rendered the
              primary action as the quietest control on it. `Button`'s own
              docstring names this row as one of the four that cost. */}
          <Button type="submit" tone="primary" busy={busy} busyLabel="Saving">
            {submitLabel}
          </Button>
        </div>
      </section>
    </form>
  )
}

/** One automatic trigger: its switch, and the threshold it fires at. */
function ConditionRow({
  condition,
  onChange,
}: {
  condition: Condition
  onChange: (next: Condition) => void
}): JSX.Element {
  return (
    <div className="app-gate__row">
      <label className="ar-switch">
        <input
          type="checkbox"
          aria-label={condition.label}
          checked={condition.enabled}
          onChange={(event) => {
            onChange({ ...condition, enabled: event.target.checked })
          }}
        />
        <span className="ar-switch__track" aria-hidden="true" />
      </label>
      <div style={{ flex: 1 }}>
        <strong>{condition.label}</strong>
        {/* SEO-UI-75. The sentence saying what switching it on does, from the
            API — `app/runs/triggers.CONDITIONS` is where the list, its order
            and now its copy are decided, and a second copy in TypeScript would
            be the one that disagreed about which switch does what. The scope
            stays beside it: the description says what the run does and this
            says how much of a run it is, which is what the tenant is paying
            for. */}
        <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          {condition.description}
        </div>
        {condition.enabled ? (
          <div className="ar-row" style={{ marginTop: 'var(--ar-space-2)' }}>
            <label className="ar-field__label" htmlFor={`threshold-${condition.name}`}>
              Threshold
            </label>
            <input
              id={`threshold-${condition.name}`}
              className="ar-input ar-num"
              type="number"
              style={{ maxWidth: '6rem' }}
              min={condition.minimum}
              max={condition.maximum}
              value={condition.threshold}
              onChange={(event) => {
                // Not clamped here. The server clamps on write and returns what it
                // stored, so a typed 0 comes back as the floor rather than being
                // silently corrected under the tenant's cursor mid-keystroke.
                onChange({ ...condition, threshold: Number(event.target.value) })
              }}
            />
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
              between {condition.minimum} and {condition.maximum}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
